import { Fragment, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import ArrowIcon from '../../../assets/images/catalogue/arrowIcon';
import ChargerModule from '../../../components/chargerModule';
import { getOrganizationData, getSpecialBusinessNameTypes, updateCare } from '../../../services/bussines';
import { IBasicProductsCare, IorganizationBusinessData, ISpecialBusinessNameTypes } from '../../../types/bussines';
import errorMessage from '../../../utils/errorMessage';
import {
  BodyTabs,
  OrgDataSection,
  OrgDetailsHeader,
  OrgUsersSection,
  NudosCareTab,
  BillingCountryTab,
  OrganizationLoader,
  OrgDataAccount,
  CouponsTab
} from './Components';
import { countriesList } from '../../../services/countriesList';
import { ICountryDetail } from '../../../types/countries';
import alertMessage from '../../../utils/alertMessage';
import { displayErrorNotification } from '../../../utils/displayNudosStandardNotifications';
import ConfigurationTab from './Components/ConfigurationTab/ConfigurationTab';
import './OrganizationDetail.scss';

interface ITab {
  id: number;
  name: string;
}

const OrganizationDetail = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const location = useLocation();

  const [organizationData, setOrganizationData] = useState<IorganizationBusinessData>();
  const [dataWasUpdated, setDataWasUpdated] = useState(0);
  const [selectTab, setSelectTab] = useState<ITab>({ id: 1, name: 'General' });
  const [generalLoader, setGeneralLoader] = useState<boolean>(false);
  const [countriesLoader, setCountriesLoader] = useState<boolean>(false);
  const [countriesListDetail, setCountriesListDetail] = useState<ICountryDetail[]>();
  const [dinamicProductList, setDinamicProductList] = useState<IBasicProductsCare[]>([]);
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [loadingUpdateCare, setLoadingUpdateCare] = useState<boolean>(true);
  const [specialBusinessTypes, setSpecialBusinessTypes] = useState<ISpecialBusinessNameTypes>();

  const originUrl = (location?.state as { originUrl: string })?.originUrl;
  const infoSideBar = { position: 'organization' };
  const headerInfo = { title: 'Empresa', subtitle: 'Detalle' };
  const returnUrl = originUrl || '/organization/list';

  const optionsTabs: ITab[] = [
    { id: 1, name: 'General' },
    { id: 2, name: 'Cuenta' },
    { id: 3, name: 'Facturación' },
    { id: 4, name: 'Herramientas' },
    { id: 5, name: 'Cupones' },
    { id: 6, name: 'Configuración' }
  ];

  const getOrganizationDetails = async (disabledLoader?: boolean) => {
    if (!disabledLoader) {
      setGeneralLoader(true);
    }
    try {
      const organizationDetails = await getOrganizationData(organizationId);
      if (organizationDetails) {
        setOrganizationData({ ...organizationDetails });
      }
    } catch (error) {
      errorMessage();
    } finally {
      setGeneralLoader(false);
      setLoadingUpdateCare(false);
    }
  };

  const changeTab = (e: ITab) => {
    if (e) setSelectTab(e);
  };

  const getCountriesList = async () => {
    setCountriesLoader(true);
    try {
      const myList = await countriesList();
      setCountriesListDetail(myList);
    } catch (error) {
      errorMessage();
    } finally {
      setCountriesLoader(false);
    }
  };

  const careProductsUpdate = async (e: IBasicProductsCare[]) => {
    setLoadingUpdateCare(true);
    try {
      await updateCare(e);
      await getOrganizationDetails(true);
      setDinamicProductList([]);
      setIsEditable(false);
      alertMessage('Los planes de Nudos Care se han actualizado correctamente');
    } catch (error) {
      setLoadingUpdateCare(false);
    }
  };

  const cancelUpdate = async () => {
    setLoadingUpdateCare(true);
    try {
      await getOrganizationDetails(true);
      setDinamicProductList([]);
      setIsEditable(false);
    } catch (error) {
      setLoadingUpdateCare(false);
    }
  };

  const updateWithoutLoaders = async () => {
    try {
      await getOrganizationDetails(true);
    } catch (error) {
      errorMessage();
    }
  };

  const getSpecialBusinessType = async () => {
    try {
      const specialBusinessType = await getSpecialBusinessNameTypes();
      if (specialBusinessType) setSpecialBusinessTypes(specialBusinessType);
    } catch (error) {
      displayErrorNotification();
    }
  };

  useEffect(() => {
    getSpecialBusinessType();
  }, []);

  useEffect(() => {
    getCountriesList();
  }, []);

  useEffect(() => {
    organizationId && getOrganizationDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataWasUpdated]);

  return (
    <ChargerModule infoSideBar={infoSideBar} headerInfo={headerInfo}>
      <div className="organizationDetail">
        <Link className="returnArrow" to={returnUrl}>
          <ArrowIcon fill="#070F21" />
        </Link>
        {countriesLoader || generalLoader ? (
          <OrganizationLoader />
        ) : (
          <>
            {organizationData && (
              <OrgDetailsHeader
                organizationData={organizationData}
                optionsTabs={optionsTabs}
                selectTab={selectTab}
                changeTab={changeTab}
              />
            )}
            {selectTab.id === 1 && (
              <Fragment>
                {organizationData && (
                  <OrgDataSection
                    organizationData={organizationData}
                    organizationId={organizationId}
                    updateWithoutLoaders={updateWithoutLoaders}
                  />
                )}
                {organizationData?.users && (
                  <OrgUsersSection
                    orgUsers={organizationData.users}
                    dataWasUpdated={dataWasUpdated}
                    setDataWasUpdated={setDataWasUpdated}
                  />
                )}
              </Fragment>
            )}
            {selectTab.id === 2 && organizationData && (
              <BodyTabs>
                <OrgDataAccount organizationData={organizationData} updateWithoutLoaders={updateWithoutLoaders} />
              </BodyTabs>
            )}
            {selectTab.id === 3 && (
              <BodyTabs>
                <BillingCountryTab
                  countriesList={countriesListDetail || []}
                  billingData={organizationData?.billingData}
                  organizationName={organizationData?.name || ''}
                  updateWithoutLoaders={getOrganizationDetails}
                  specialBusinessTypes={specialBusinessTypes}
                />
              </BodyTabs>
            )}
            {selectTab.id === 4 && (
              <BodyTabs>
                <NudosCareTab
                  productsCare={organizationData?.productsCare || []}
                  productsPerCategory={organizationData?.productsPerCategory || []}
                  careProductsUpdate={careProductsUpdate}
                  setDinamicProductList={setDinamicProductList}
                  dinamicProductList={dinamicProductList}
                  setIsEditable={setIsEditable}
                  isEditable={isEditable}
                  loadingUpdateCare={loadingUpdateCare}
                  cancelUpdate={cancelUpdate}
                />
              </BodyTabs>
            )}
            {selectTab.id === 5 && (
              <BodyTabs>
                <CouponsTab organizationData={organizationData} />
              </BodyTabs>
            )}
            {selectTab.id === 6 && (
              <BodyTabs>
                <ConfigurationTab organizationData={organizationData} />
              </BodyTabs>
            )}
          </>
        )}
      </div>
    </ChargerModule>
  );
};

export default OrganizationDetail;
